import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ShoppingBag } from "lucide-react";
import { NAV, CHURCH } from "../lib/data";
import { Btn } from "./ui-kit";
import { useCart } from "../lib/cart";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-sm" : "bg-white/70 backdrop-blur-md"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
          <img src={CHURCH.logo} alt="One Love Ministries" className="w-10 h-10 object-contain group-hover:scale-105 transition-transform" />
          <span className="font-display font-extrabold tracking-tight text-[15px] sm:text-base leading-none">
            ONE LOVE<span className="text-[#E63946]"> MINISTRIES</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={`text-sm relative transition-colors hover:text-[#E63946] ${
                pathname === n.to ? "text-[#E63946]" : "text-[#0A0A0A]"
              }`}
            >
              {n.label}
              {pathname === n.to && <span className="absolute -bottom-1.5 left-0 w-full h-px bg-[#E63946]" />}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button onClick={() => setCartOpen(true)} data-testid="nav-cart-btn" aria-label="Cart"
            className="relative grid place-items-center w-11 h-11 rounded-full hover:bg-black/5 transition-colors">
            <ShoppingBag size={20} />
            {count > 0 && <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E63946] text-white text-[10px] font-bold grid place-items-center">{count}</span>}
          </button>
          <Btn to="/plan-your-visit" testid="nav-plan-visit-btn">Plan Your Visit</Btn>
        </div>

        <div className="lg:hidden flex items-center">
          <button onClick={() => setCartOpen(true)} data-testid="nav-cart-btn-mobile" aria-label="Cart"
            className="relative grid place-items-center w-11 h-11">
            <ShoppingBag size={22} />
            {count > 0 && <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E63946] text-white text-[10px] font-bold grid place-items-center">{count}</span>}
          </button>
          <button
            className="grid place-items-center w-11 h-11 -mr-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-black/5 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`py-3 text-lg font-display font-medium ${pathname === n.to ? "text-[#E63946]" : "text-[#0A0A0A]"}`}
                >
                  {n.label}
                </Link>
              ))}
              <div className="flex flex-col gap-3 mt-4">
                <Btn to="/plan-your-visit" className="w-full">Plan Your Visit</Btn>
                <Btn href={CHURCH.phoneHref} variant="outline" className="w-full"><Phone size={16} /> Call Us</Btn>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
